/* istanbul ignore file */
import {
  AdviesBoxFormik,
  LabeledTextInput,
  LabeledCheckBox,
  CheckBox,
  LabeledInput,
  TextEditor,
  Label,
  useRequestInit
} from "adviesbox-shared";
import { FormikProps, getIn, setIn, useFormikContext } from "formik";
import React, { ReactElement, useMemo, useState, useEffect } from "react";
import { LabelValuePairs } from "../../types";
import Modal from "../modal/Modal";
import { RapportageSamenstellenState, RapportageElementType } from "./infra/rapportage-samenstellen-types";
import { rapportageSamenstellenSchema } from "./rapportage-samenstellen-schema";
import { Button } from "react-bootstrap";
import { RapportageElementSoort } from "./infra/rapportage-structuur-types";
import { getRapportageTags } from "./rapportage-tags-api";
import { ReplaceableTagInfo } from "../../../.generated/rapportage/rapportagetypes";

type TitelAanpassen = {
  onSave?: (data: any) => void;
  closeModal?: () => void;
  parent: string;
  adoptionList?: LabelValuePairs;
};

const findElement = (elements: RapportageElementType[], targetCode: number): RapportageElementType | null => {
  for (const element of elements) {
    if (element.code === targetCode) {
      return element;
    }

    // If not found, search in nested 'elementen' recursively
    const foundInSubElement = findElement(element.elementen, targetCode);
    if (foundInSubElement) {
      return foundInSubElement;
    }
  }

  return null; // Return null if the code is not found
};

export const TitelAanpassenModal = ({ onSave, closeModal, parent }: TitelAanpassen): ReactElement => {
  const { values } = useFormikContext<RapportageSamenstellenState>();
  const elementData: RapportageElementType = getIn(values, parent);
  const defaultElement = useMemo(() => findElement(values.defaultStructuur.elementen, elementData.code), [
    values.defaultStructuur.elementen,
    elementData.code
  ]);
  const labelColSize = 4;
  const { settings, user } = useRequestInit();
  const [tags, setTags] = useState<ReplaceableTagInfo[]>([]);

  useEffect(() => {
    const fetchTags = async (): Promise<void> => {
      try {
        const fetchedTags = await getRapportageTags(settings, user);
        setTags(fetchedTags);
      } catch (error) {
        setTags([]);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchTags();
  }, [settings, user]);

  const restoreButton = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  ): ReactElement => {
    return (
      <Button
        disabled={
          elementData.naam === (defaultElement?.naam ?? "") &&
          elementData.naamWeergevenAlsTitel === defaultElement?.naamWeergevenAlsTitel &&
          elementData.inhoud === (defaultElement?.inhoud ?? "") &&
          !elementData.perAdviesAanpasbaar
        }
        onClick={() => {
          setFieldValue(`${parent}.naam`, defaultElement?.naam ?? "");
          setFieldValue(`${parent}.naamWeergevenAlsTitel`, defaultElement?.naamWeergevenAlsTitel);
          setFieldValue(`${parent}.inhoud`, defaultElement?.inhoud ?? "");
          setFieldValue(`${parent}.kolommen`, defaultElement?.kolommen ?? "");
          setFieldValue(`${parent}.perAdviesAanpasbaar`, false);
        }}
        data-testid={`${parent}-testid`}
      >
        Herstel naar standaardwaarden
      </Button>
    );
  };

  const save = (values: any): any => {
    let newValues = values;

    if (values.movetoparent) {
      const elementToMove = getIn(newValues, parent);
      const arrayToAddTo = getIn(newValues, values.movetoparent);

      const nameList = parent.split(".");

      nameList.pop();

      const parentListName = nameList.join(".");

      const parentList = getIn(newValues, parentListName).elementen;

      const filteredParentList = parentList
        .filter((c: any) => {
          return c !== elementToMove;
        })
        .map((c: any, i: number) => ({ ...c, volgnummer: i + 1 }));

      newValues = setIn(newValues, `${parentListName}.elementen`, filteredParentList);
      newValues = setIn(
        newValues,
        `${values.movetoparent}.elementen`,
        [...arrayToAddTo.elementen, elementToMove].map((c: any, i: number) => ({ ...c, volgnummer: i + 1 }))
      );
    }
    return newValues;
  };

  const body = (): ReactElement => {
    return (
      <>
        <LabeledTextInput
          caption={"Titel"}
          labelColSize={labelColSize}
          fieldSize={"full-size"}
          name={`${parent}.naam`}
        />
        <LabeledCheckBox
          caption="Titel weergeven als kop"
          labelColSize={labelColSize}
          name={`${parent}.naamWeergevenAlsTitel`}
        ></LabeledCheckBox>
       {!!elementData.promptNaam  && (<LabeledCheckBox
          caption="AI toepassen"
          labelColSize={labelColSize}
          name={`${parent}.gebruikAi`}
        >AI gebruiken</LabeledCheckBox> )}
        {elementData.soortElement === RapportageElementSoort.Tekstblok && (
          <>
            {elementData.gebruikAi && (
              <>
                <LabeledTextInput
                  caption={"Prompt naam"}
                  labelColSize={labelColSize}
                  fieldSize={"full-size"}
                  name={`${parent}.promptNaam`}
                />
                <div className="form-group form-row">
                  <div className={`col-${labelColSize}`}>
                    <Label caption={"Prompt aanvulling"} name={`${parent}.promptAanvulling`} />
                  </div>
                  <div className={`col-${8}`}>
                    <TextEditor 
                      name={`${parent}.promptAanvulling`}
                      useTextEditor={true} 
                      rows={11}
                      tags={tags.map(tagInfo => ({ label: tagInfo.description || tagInfo.tag || "", value: tagInfo.tag || "" }))}
                    />
                  </div>
                </div>
              </>
            )}
            {!elementData.gebruikAi && elementData.inhoudBeschikbaar && tags.length > 0 && (
              <>
                <div className="form-group form-row">
                  <div className={`col-${labelColSize}`}>
                    <Label caption={"Inhoud"} name={`${parent}.inhoud`} />
                  </div>
                  <div className={`col-${8}`}>
                    <TextEditor 
                      name={`${parent}.inhoud`}
                      useTextEditor={true} 
                      rows={11}
                      tags={tags.map(tagInfo => ({ label: tagInfo.description || tagInfo.tag || "", value: tagInfo.tag || "" }))}
                    />
                  </div>
                </div>
                <LabeledCheckBox
                  caption="Per advies aanpasbaar"
                  labelColSize={labelColSize}
                  name={`${parent}.perAdviesAanpasbaar`}
                ></LabeledCheckBox>
              </>
            )}
          </>
        )}

        {elementData.soortElement === RapportageElementSoort.Tabel && (defaultElement?.kolommen?.length ?? 0) > 0 && (
          <LabeledInput caption="Kolommen" labelColSize={labelColSize} name="dummy">
            {defaultElement?.kolommen.map((x, index) => (
              <CheckBox
                key={`${parent}.kolommen[${index}]`}
                caption={x.kolomTitel}
                name={`${parent}.kolommen[${index}].selected`}
              ></CheckBox>
            ))}
          </LabeledInput>
        )}

        {elementData.soortElement === RapportageElementSoort.Grafiek && (defaultElement?.kolommen?.length ?? 0) > 0 && (
          <LabeledInput caption="Legenda" labelColSize={labelColSize} name="dummy">
            {defaultElement?.kolommen.map((x, index) => (
              <CheckBox
                key={`${parent}.kolommen[${index}]`}
                caption={x.kolomTitel}
                name={`${parent}.kolommen[${index}].selected`}
              ></CheckBox>
            ))}
          </LabeledInput>
        )}
      </>
    );
  };

  return (
    <AdviesBoxFormik<RapportageSamenstellenState>
      initialValues={{ ...values }}
      validationSchema={rapportageSamenstellenSchema}
      onSave={values => {
        const newValues = save(values);
        onSave && onSave(newValues);
      }}
      closeModal={closeModal}
      render={({ submitForm, setFieldValue, values }: FormikProps<RapportageSamenstellenState>): ReactElement => (
        <>
          <Modal
            title="Titel aanpassen"
            body={body()}
            onSubmitClick={submitForm}
            onCancelClick={closeModal}
            prependToFooter={restoreButton(setFieldValue)}
          />
        </>
      )}
    />
  );
};
